// bestaetigen.tsx — Rückfragen, Hinweise und Texteingaben im KMVS-Design statt
// `window.confirm` / `window.alert` / `window.prompt` (User-Entscheidung
// 30.09.2026: nie Browser-Dialoge, nie Fluent-Dialoge).
//
// `bestaetigen()` und `hinweisen()` liefern ein Promise wie die Browser-
// Varianten, sind aber nicht blockierend: aus `if (!window.confirm(t)) return;`
// wird `if (!(await bestaetigen({ ... }))) return;`. Sie funktionieren auch
// außerhalb von React-Komponenten (Hilfsfunktionen wie persistRelations):
// Der Dialog bekommt für die Dauer der Frage eine eigene kleine React-Wurzel
// und räumt sie danach wieder ab. Eine Provider-Einbindung in App.tsx ist
// deshalb nicht nötig.
//
// Die Optik kommt aus `ConfirmDialog` (Portal, REFINED-Tokens). Die Ebene liegt
// über allem (Z_LAYERS.windowConfirm), weil die Frage auch aus Fenstern,
// Dialogen und über dem CallWidget heraus kommen kann.

import * as React from "react";
import { useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { ConfirmDialog } from "../ConfirmDialog";
import { I } from "../kvpIcons";
import { Z_LAYERS } from "../../windowManager/zLayers";
import { REFINED } from "../../risikoanalyseStep/tokens";
import { aktivesDokument } from "../../windowManager/browserFenster/fensterRegister";
import { BrowserFensterHuelle } from "../../windowManager/browserFenster/BrowserFensterHuelle";

export interface BestaetigenOptionen {
  titel: string;
  /** Fließtext oder beliebiger Inhalt. Zeilenumbrüche (`\n`) bleiben stehen. */
  text: React.ReactNode;
  /** Beschriftung des Bestätigen-Knopfs (Standard „Bestätigen"). */
  ja?: string;
  /** Beschriftung des Abbrechen-Knopfs (Standard „Abbrechen"). */
  nein?: string;
  /** `danger` = rot (Löschen), `primary` = Aubergine (Standard). */
  ton?: "danger" | "primary";
  /** Kopf-Symbol; Standard je nach Ton (Papierkorb bzw. Hinweis). */
  symbol?: React.ReactNode;
}

export interface HinweisOptionen {
  titel: string;
  text: React.ReactNode;
  /** Beschriftung des einzigen Knopfs (Standard „OK"). */
  ok?: string;
  ton?: "danger" | "primary";
  symbol?: React.ReactNode;
}

type Anzeige = {
  titel: string;
  text: React.ReactNode;
  ja: string;
  nein: string | null;
  ton: "danger" | "primary";
  symbol: React.ReactNode;
};

const textKnoten = (text: React.ReactNode): React.ReactNode =>
  typeof text === "string" ? <span style={{ whiteSpace: "pre-line" }}>{text}</span> : text;

export interface EingabeOptionen {
  titel: string;
  /** Erklärung über dem Eingabefeld (optional). */
  text?: React.ReactNode;
  /** Vorbelegter Wert. */
  vorgabe?: string;
  /** Feldbeschriftung über dem Eingabefeld. */
  label?: string;
  platzhalter?: string;
  /** Beschriftung des Übernehmen-Knopfs (Standard „Übernehmen"). */
  ja?: string;
  /** Beschriftung des Abbrechen-Knopfs (Standard „Abbrechen"). */
  nein?: string;
  /** Fehlermeldung, solange der Wert nicht passt; `null` = in Ordnung. */
  pruefen?: (wert: string) => string | null;
  /** `danger` = roter Knopf und Papierkorb (z. B. Löschen mit Grund); Standard `primary`. */
  ton?: "danger" | "primary";
}

/**
 * Zeigt einen Dialog in einer eigenen React-Wurzel und löst mit der Antwort
 * auf. `inhalt` bekommt `fertig` und rendert den Dialog. T-287: exportiert
 * (als `imEigenenDialog`), damit weitere eigene Popups dieselbe Mechanik nutzen.
 */
export function imEigenenDialog<T>(ohneDom: T, inhalt: (fertig: (antwort: T) => void) => React.ReactElement): Promise<T> {
  return wurzel(ohneDom, inhalt);
}

function wurzel<T>(ohneDom: T, inhalt: (fertig: (antwort: T) => void) => React.ReactElement): Promise<T> {
  if (typeof document === "undefined") return Promise.resolve(ohneDom);
  return new Promise<T>((resolve) => {
    // Gefragt wird dort, wo der Nutzer gerade arbeitet — auch in einem
    // ausgelagerten Browserfenster (Fenster auf dem zweiten Bildschirm).
    const dok = aktivesDokument();
    const host = dok.createElement("div");
    host.setAttribute("data-kvp-bestaetigen", "");
    dok.body.appendChild(host);
    const root = createRoot(host);
    let erledigt = false;
    const fertig = (antwort: T) => {
      if (erledigt) return;
      erledigt = true;
      resolve(antwort);
      // Erst nach dem aktuellen Ereignis abbauen — React mag kein
      // unmount mitten im eigenen Event-Handler.
      window.setTimeout(() => {
        root.unmount();
        host.remove();
      }, 0);
    };
    root.render(dok === document ? inhalt(fertig) : <BrowserFensterHuelle dok={dok}>{inhalt(fertig)}</BrowserFensterHuelle>);
  });
}

/** Zeigt den Dialog in einer eigenen React-Wurzel und löst mit der Antwort auf. */
function zeigen(a: Anzeige): Promise<boolean> {
  return wurzel<boolean>(false, (fertig) => (
    <ConfirmDialog
      open
      title={a.titel}
      message={textKnoten(a.text)}
      confirmLabel={a.ja}
      cancelLabel={a.nein}
      tone={a.ton}
      icon={a.symbol}
      zIndex={Z_LAYERS.windowConfirm}
      onConfirm={() => fertig(true)}
      // Ein reiner Hinweis kennt kein „Nein": ESC/Hintergrund heißt „gelesen".
      onCancel={() => fertig(a.nein === null)}
    />
  ));
}

/** Rückfrage mit Bestätigen/Abbrechen. `true` = bestätigt. */
export function bestaetigen(o: BestaetigenOptionen): Promise<boolean> {
  const ton = o.ton ?? "primary";
  return zeigen({
    titel: o.titel,
    text: o.text,
    ja: o.ja ?? "Bestätigen",
    nein: o.nein ?? "Abbrechen",
    ton,
    symbol: o.symbol ?? (ton === "danger" ? I.trash : I.warnung),
  });
}

/** Hinweis mit nur einem Knopf — Ersatz für `window.alert`. */
export async function hinweisen(o: HinweisOptionen): Promise<void> {
  const ton = o.ton ?? "primary";
  await zeigen({
    titel: o.titel,
    text: o.text,
    ja: o.ok ?? "OK",
    nein: null,
    ton,
    symbol: o.symbol ?? I.warnung,
  });
}

/** Eingabefeld im Dialog — Zustand, Prüfung, Enter = Übernehmen. */
const EingabeDialog: React.FC<{ o: EingabeOptionen; fertig: (wert: string | null) => void }> = ({ o, fertig }) => {
  const [wert, setWert] = useState(o.vorgabe ?? "");
  const [fehler, setFehler] = useState<string | null>(null);
  const feldRef = useRef<HTMLInputElement | null>(null);
  const feldId = React.useId();

  React.useEffect(() => {
    const t = window.setTimeout(() => {
      feldRef.current?.focus();
      feldRef.current?.select();
    }, 0);
    return () => window.clearTimeout(t);
  }, []);

  const uebernehmen = () => {
    const meldung = o.pruefen?.(wert) ?? null;
    if (meldung) {
      setFehler(meldung);
      feldRef.current?.focus();
      return;
    }
    fertig(wert);
  };

  return (
    <ConfirmDialog
      open
      title={o.titel}
      message={
        <>
          {o.text != null && <div style={{ marginBottom: 12 }}>{textKnoten(o.text)}</div>}
          {o.label && (
            <label
              htmlFor={feldId}
              style={{
                display: "block",
                marginBottom: 6,
                fontSize: REFINED.fs.xxs,
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: REFINED.ink2,
              }}
            >
              {o.label}
            </label>
          )}
          <input
            id={feldId}
            ref={feldRef}
            type="text"
            value={wert}
            placeholder={o.platzhalter}
            aria-label={o.label ? undefined : o.titel}
            aria-invalid={fehler ? true : undefined}
            onChange={(e) => {
              setWert(e.target.value);
              if (fehler) setFehler(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                uebernehmen();
              }
            }}
            style={{
              width: "100%",
              boxSizing: "border-box",
              font: "inherit",
              fontSize: REFINED.fs.md,
              color: REFINED.ink,
              padding: "9px 12px",
              borderRadius: 10,
              border: `1px solid ${fehler ? REFINED.err : REFINED.line}`,
              background: REFINED.fieldBg,
              outline: "none",
              boxShadow: fehler ? REFINED.errRing : REFINED.focusRing,
            }}
          />
          {fehler && (
            <span role="alert" style={{ display: "block", marginTop: 6, fontSize: REFINED.fs.xs, color: REFINED.err }}>
              {fehler}
            </span>
          )}
        </>
      }
      confirmLabel={o.ja ?? "Übernehmen"}
      cancelLabel={o.nein ?? "Abbrechen"}
      tone={o.ton ?? "primary"}
      icon={o.ton === "danger" ? I.trash : I.edit}
      zIndex={Z_LAYERS.windowConfirm}
      focusConfirm={false}
      onConfirm={uebernehmen}
      onCancel={() => fertig(null)}
    />
  );
};

/**
 * Texteingabe — Ersatz für `window.prompt`. Liefert den eingegebenen Text oder
 * `null` bei Abbrechen/ESC/Klick daneben.
 */
export function eingabe(o: EingabeOptionen): Promise<string | null> {
  return wurzel<string | null>(null, (fertig) => <EingabeDialog o={o} fertig={fertig} />);
}

export interface AuswahlOptionen {
  titel: string;
  /** Erklärung über der Auswahl. */
  text?: React.ReactNode;
  /** Wählbare Werte. Bei genau einem entfällt die Auswahl — der Knopf übernimmt ihn. */
  optionen: string[];
  /** Vorgewählter Wert (Standard: keiner, bei nur einer Option diese). */
  vorgabe?: string;
  /** Beschriftung über der Auswahl. */
  label?: string;
  /** Beschriftung des Übernehmen-Knopfs (Standard „Übernehmen"). */
  ja?: string;
  /** Beschriftung des Abbrechen-Knopfs (Standard „Abbrechen"). */
  nein?: string;
}

/** Auswahl im Dialog — Optionsfelder, „Übernehmen" erst nach einer Wahl. */
const AuswahlDialog: React.FC<{ o: AuswahlOptionen; fertig: (wert: string | null) => void }> = ({ o, fertig }) => {
  const einzige = o.optionen.length === 1 ? o.optionen[0] : undefined;
  const [wert, setWert] = useState<string>(o.vorgabe ?? einzige ?? "");
  const [fehler, setFehler] = useState<string | null>(null);
  const name = React.useId();
  const uebernehmen = () => {
    if (!wert) {
      setFehler("Bitte eine Auswahl treffen.");
      return;
    }
    fertig(wert);
  };
  return (
    <ConfirmDialog
      open
      title={o.titel}
      message={
        <>
          {o.text != null && <div style={{ marginBottom: einzige ? 0 : 12 }}>{textKnoten(o.text)}</div>}
          {!einzige && (
            <div role="radiogroup" aria-label={o.label ?? o.titel} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {o.label && (
                <span
                  style={{
                    fontSize: REFINED.fs.xxs,
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: REFINED.ink2,
                  }}
                >
                  {o.label}
                </span>
              )}
              {o.optionen.map((opt) => (
                <label key={opt} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", color: REFINED.ink }}>
                  <input
                    type="radio"
                    name={name}
                    value={opt}
                    checked={wert === opt}
                    onChange={() => {
                      setWert(opt);
                      setFehler(null);
                    }}
                  />
                  {opt}
                </label>
              ))}
              {fehler && (
                <span role="alert" style={{ fontSize: REFINED.fs.xs, color: REFINED.err }}>
                  {fehler}
                </span>
              )}
            </div>
          )}
        </>
      }
      confirmLabel={o.ja ?? "Übernehmen"}
      cancelLabel={o.nein ?? "Abbrechen"}
      tone="primary"
      icon={I.warnung}
      zIndex={Z_LAYERS.windowConfirm}
      focusConfirm={!!einzige}
      onConfirm={uebernehmen}
      onCancel={() => fertig(null)}
    />
  );
};

/**
 * Auswahl aus festen Werten im KMVS-Popup. Liefert den gewählten Wert oder
 * `null` bei Abbrechen/ESC/Klick daneben.
 */
export function auswahl(o: AuswahlOptionen): Promise<string | null> {
  return wurzel<string | null>(null, (fertig) => <AuswahlDialog o={o} fertig={fertig} />);
}

/**
 * Hook-Form für Komponenten: `const { bestaetigen, hinweisen, eingabe } = useBestaetigen();`.
 * Die Funktionen sind stabil (kein Neurendern, keine Abhängigkeit in
 * `useCallback`/`useEffect` nötig).
 */
export function useBestaetigen(): {
  bestaetigen: typeof bestaetigen;
  hinweisen: typeof hinweisen;
  eingabe: typeof eingabe;
  auswahl: typeof auswahl;
} {
  return STABIL;
}
const STABIL = { bestaetigen, hinweisen, eingabe, auswahl } as const;
